import React, { useState, useEffect } from 'react';
import API from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiPlus, FiUserCheck, FiUserX, FiShield } from 'react-icons/fi';
import { formatCreatedAt } from '../../utils/dateUtils';

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    password: '', role: 'patient', city: '',
  });

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (roleFilter) params.role = roleFilter;
      if (search) params.search = search;
      const res = await API.get('/admin/users', { params });
      setUsers(res.data.data.users);
      setPagination({ page: res.data.data.pagination.page, totalPages: res.data.data.pagination.totalPages });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [roleFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(1);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post('/admin/users', form);
      toast.success('Utilisateur créé.');
      setShowModal(false);
      setForm({ first_name: '', last_name: '', email: '', phone: '', password: '', role: 'patient', city: '' });
      fetchUsers(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur.');
    }
  };

  const toggleActive = async (userId) => {
    try {
      const res = await API.put(`/admin/users/${userId}/toggle-active`);
      toast.success(res.data.message);
      fetchUsers(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur.');
    }
  };

  const changeRole = async (userId, newRole) => {
    if (!window.confirm(`Changer le rôle en "${newRole}" ?`)) return;
    try {
      const res = await API.put(`/admin/users/${userId}/role`, { role: newRole });
      toast.success(res.data.message);
      fetchUsers(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur.');
    }
  };

  const roleLabels = { patient: 'Patient', receptionniste: 'Réceptionniste', medecin: 'Médecin', caissier: 'Caissier', admin: 'Admin' };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 600 }}>Gestion des utilisateurs</h2>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Gérez les comptes patients et administrateurs</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><FiPlus /> Nouvel utilisateur</button>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flex: 1 }}>
            <input className="form-input" placeholder="Rechercher par nom, email, téléphone..."
              value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 300 }} />
            <button type="submit" className="btn btn-outline">Rechercher</button>
          </form>
          <select className="form-select" style={{ width: 160 }} value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">Tous les rôles</option>
            <option value="patient">Patients</option>
            <option value="receptionniste">Réceptionnistes</option>
            <option value="medecin">Médecins</option>
            <option value="caissier">Caissiers</option>
            <option value="admin">Admins</option>
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-page"><div className="spinner"></div></div>
        ) : users.length === 0 ? (
          <div className="empty-state"><h3>Aucun utilisateur trouvé</h3></div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Nom complet</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Ville</th>
                    <th>Rôle</th>
                    <th>Statut</th>
                    <th>Inscrit le</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</td>
                      <td>{u.email}</td>
                      <td>{u.phone}</td>
                      <td>{u.city || '-'}</td>
                      <td><span className={`badge badge-${u.role}`}>{roleLabels[u.role]}</span></td>
                      <td>
                        <span className={`badge ${u.is_active ? 'badge-success' : 'badge-failed'}`}>
                          {u.is_active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td>{formatCreatedAt(u)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-success'}`}
                            onClick={() => toggleActive(u.id)}
                            title={u.is_active ? 'Désactiver' : 'Activer'}
                          >
                            {u.is_active ? <FiUserX /> : <FiUserCheck />}
                          </button>
                          <select
                            className="form-select"
                            style={{ width: 140, padding: '4px 8px', fontSize: 13 }}
                            value={u.role}
                            onChange={(e) => changeRole(u.id, e.target.value)}
                          >
                            <option value="patient">Patient</option>
                            <option value="receptionniste">Réceptionniste</option>
                            <option value="medecin">Médecin</option>
                            <option value="caissier">Caissier</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button disabled={pagination.page <= 1} onClick={() => fetchUsers(pagination.page - 1)}>Précédent</button>
                <span style={{ fontSize: 14, color: 'var(--gray-600)' }}>Page {pagination.page} / {pagination.totalPages}</span>
                <button disabled={pagination.page >= pagination.totalPages} onClick={() => fetchUsers(pagination.page + 1)}>Suivant</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Création */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvel utilisateur</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Prénom *</label>
                    <input className="form-input" value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nom *</label>
                    <input className="form-input" value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Téléphone *</label>
                    <input className="form-input" placeholder="+243..." value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ville</label>
                    <input className="form-input" value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Mot de passe *</label>
                    <input type="password" className="form-input" minLength={6} value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rôle</label>
                    <select className="form-select" value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}>
                      <option value="patient">Patient</option>
                      <option value="receptionniste">Réceptionniste</option>
                      <option value="medecin">Médecin</option>
                      <option value="caissier">Caissier</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Créer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
